import { HTMLAttributes } from "react";
import { cn } from "@/shared/lib/utils";
import { useNumbersDocumentNumberShowQuery } from "@/services/backend/numbers/api/document-number-api";
import { Skeleton } from "@/shared/components/ui/skeleton";
import {
  is404,
  parseRTKQueryError,
} from "@/shared/components/errors/parse-r-t-k-query-error";
import { DocumentNumber as DocumentNumberInterface } from "@/services/backend/numbers/types/document-number";
import t from "@/lang/lang";

export interface DocumentNumberByIdProps extends HTMLAttributes<HTMLSpanElement> {
  documentId: string;
  skip?: boolean;
}

export function DocumentNumberById({
  documentId,
  skip = false,
  className,
  ...props
}: DocumentNumberByIdProps) {
  const {
    data: number,
    isLoading,
    error,
  } = useNumbersDocumentNumberShowQuery({ documentId }, { skip });

  if (skip) {
    return (
      <span className={cn("font-mono whitespace-nowrap", className)} {...props}>
        {t("- keine Nummer -")}
      </span>
    );
  }

  if (isLoading) {
    return <Skeleton className="h-2 w-52" />;
  }

  if (error) {
    if (is404(error)) {
      return (
        <span className={cn("font-mono whitespace-nowrap", className)}>
          {t("- keine Nummer -")}
        </span>
      );
    }

    const err = parseRTKQueryError(error);
    return <span className="whitespace-nowrap">{err?.title}</span>;
  }

  return <DocumentNumber number={number!} className={className} {...props} />;
}

export interface DocumentNumberProps extends HTMLAttributes<HTMLSpanElement> {
  number: DocumentNumberInterface;
}

export function DocumentNumber({
  number,
  className,
  ...props
}: DocumentNumberProps) {
  return (
    <span className={cn("font-mono whitespace-nowrap", className)} {...props}>
      {number.string}
    </span>
  );
}
